import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  useAiDocumentReview,
  ReviewInputField,
  ReviewResultField,
} from "@/hooks/useAiDocumentReview";
import { useAiSuggestionLog, AiSuggestionTarget } from "@/hooks/useAiSuggestionLog";

interface AiDocumentReviewDialogProps extends AiSuggestionTarget {
  documentName: string;
  /** Hämtar alla fritextfält i dokumentet just nu */
  collectFields: () => ReviewInputField[];
  /** Skriver tillbaka de förslag användaren valt */
  applyFields: (updates: { id: string; text: string }[]) => void | Promise<void>;
  triggerLabel?: string;
  variant?: "outline" | "ghost" | "secondary" | "default";
  size?: "sm" | "default";
}

/**
 * Knapp + panel som låter AI:n språkgranska hela dokumentet och visa
 * original mot förslag per avsnitt.
 */
export const AiDocumentReviewDialog: React.FC<AiDocumentReviewDialogProps> = ({
  documentName,
  collectFields,
  applyFields,
  triggerLabel = "Granska med AI",
  variant = "outline",
  size = "sm",
  documentType,
  documentId,
  projectId,
}) => {
  const { review, isReviewing } = useAiDocumentReview();
  const { logSuggestion } = useAiSuggestionLog();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ReviewResultField[]>([]);
  const [unchangedCount, setUnchangedCount] = useState(0);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const runReview = async () => {
    const fields = collectFields();
    const res = await review(documentName, fields);
    if (!res) return;

    const changed = res.filter((r) => r.changed);
    setResults(changed);
    setUnchangedCount(res.length - changed.length);
    setSelected(Object.fromEntries(changed.map((r) => [r.id, true])));
    setOpen(true);

    if (documentType) {
      await Promise.all(
        changed.map((r) =>
          logSuggestion(
            {
              documentType,
              documentId,
              projectId,
              fieldKey: r.id,
              fieldLabel: r.label,
            },
            r.original,
            r.polished,
            { source: "document_review" },
          ),
        ),
      );
    }
  };

  const apply = async (all: boolean) => {
    const updates = results
      .filter((r) => all || selected[r.id])
      .map((r) => ({ id: r.id, text: r.polished }));

    if (updates.length === 0) {
      toast.info("Inget valt");
      return;
    }

    await applyFields(updates);
    toast.success(`${updates.length} avsnitt uppdaterade`);
    setOpen(false);
    setResults([]);
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={runReview} disabled={isReviewing}>
        {isReviewing ? (
          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4 mr-1.5" />
        )}
        {isReviewing ? "Granskar…" : triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>AI-granskning av {documentName}</DialogTitle>
            <DialogDescription>
              AI:n rättar bara språket. Inget innehåll läggs till eller tas bort – du väljer själv
              vad som ska användas.
            </DialogDescription>
          </DialogHeader>

          {results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Inget att ändra – texterna ser redan bra ut.
            </div>
          ) : (
            <>
              <ScrollArea className="max-h-[55vh] pr-3">
                <div className="space-y-4">
                  {results.map((r) => (
                    <div key={r.id} className="rounded-md border p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={!!selected[r.id]}
                          onCheckedChange={(v) =>
                            setSelected((s) => ({ ...s, [r.id]: Boolean(v) }))
                          }
                          className="mt-1"
                        />
                        <p className="text-sm font-medium">{r.label || r.id}</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Original</p>
                          <p className="text-sm whitespace-pre-wrap">{r.original}</p>
                        </div>
                        <div className="rounded-md bg-primary/5 p-2">
                          <p className="text-xs text-muted-foreground mb-1">AI-förslag</p>
                          <p className="text-sm whitespace-pre-wrap">{r.polished}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Button type="button" size="sm" onClick={() => apply(false)}>
                  <Check className="h-4 w-4 mr-1.5" />
                  Använd markerade
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => apply(true)}>
                  Använd alla
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
                  Avbryt
                </Button>
                {unchangedCount > 0 && (
                  <Badge variant="outline">{unchangedCount} fält: inget att ändra</Badge>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AiDocumentReviewDialog;
